import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { CheckItemResult, CheckResult, MetricSource } from '@shared/types';
import { CATEGORY_LABELS } from '@shared/types';
import { pollPublicCheck } from '../publicApi';
import ScoreBadge from '../components/ScoreBadge';
import ScoreRadar from '../components/ScoreRadar';
import IssueList from '../components/IssueList';
import { scoreColor, itemSortKey, formatDate } from '../scoreUtils';

const CWV_SOURCE_LABEL: Record<MetricSource, string> = {
  field: '実測値（フィールドデータ / CrUX）',
  lab: 'ラボ値（Lighthouse推定）',
  none: '取得不可',
};

const CONSULT_MAILTO =
  'mailto:info@first-temple.co.jp?subject=SEO診断の相談';

type Phase = 'loading' | 'done' | 'error';

/** 主要3課題を抽出（fail > warn 優先、critical をさらに優先）。 */
function topIssues(items: CheckItemResult[]): CheckItemResult[] {
  return [...items]
    .filter((i) => i.status === 'fail' || i.status === 'warn')
    .sort((a, b) => itemSortKey(a.status, a.critical) - itemSortKey(b.status, b.critical))
    .slice(0, 3);
}

export default function PublicResultPage() {
  const { checkId } = useParams<{ checkId: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [result, setResult] = useState<CheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const copyResultUrl = useCallback(() => {
    const url = window.location.href;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2500);
        },
        () => window.prompt('このURLをコピーして保存してください', url),
      );
    } else {
      window.prompt('このURLをコピーして保存してください', url);
    }
  }, []);

  useEffect(() => {
    if (!checkId) return;
    let alive = true;
    setPhase('loading');
    setError(null);
    setResult(null);

    (async () => {
      try {
        const s = await pollPublicCheck(checkId);
        if (!alive) return;
        if (s.status === 'error') {
          setError(s.error ?? '診断に失敗しました。');
          setPhase('error');
        } else if (s.result) {
          setResult(s.result);
          setPhase('done');
        } else {
          setError('診断結果を取得できませんでした。');
          setPhase('error');
        }
      } catch (e) {
        if (!alive) return;
        setError(e instanceof Error ? e.message : String(e));
        setPhase('error');
      }
    })();

    return () => {
      alive = false;
    };
  }, [checkId]);

  const top3 = useMemo(
    () => (result ? topIssues(result.items) : []),
    [result],
  );

  if (phase === 'loading') {
    return (
      <div className="public-result">
        <div className="card public-result__loading">
          <div className="spinner" />
          <div>
            <strong>診断中です…</strong>
            <p className="muted small">
              PageSpeed Insights と AIによるコンテンツ評価を実行しています。
              完了まで40〜70秒ほどかかります。このままお待ちください。
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="public-result">
        <div className="alert alert--error">{error}</div>
        <div className="public-result__retry">
          <Link className="btn btn--primary" to="/">
            もう一度診断する
          </Link>
        </div>
      </div>
    );
  }

  if (!result) return null;

  return (
    <div className="public-result">
      <div className="public-result__head">
        <h1>SEO診断結果</h1>
        <a
          className="result-url"
          href={result.finalUrl ?? result.url}
          target="_blank"
          rel="noreferrer noopener"
        >
          {result.finalUrl ?? result.url}
        </a>
        <div className="muted small">診断日時: {formatDate(result.checkedAt)}</div>
      </div>

      <div className="public-save-notice card">
        <div>
          <strong>このページのURLを保存すると、いつでも同じ結果を見返せます。</strong>
          <p className="muted small">
            結果はこのURL専用に保存されています。閉じてしまっても、URLをブックマークまたはコピーしておけば再訪問できます。
          </p>
        </div>
        <button type="button" className="btn" onClick={copyResultUrl}>
          {copied ? 'コピーしました ✓' : 'このURLをコピー'}
        </button>
      </div>

      <div className="result-top card">
        <div className="result-total">
          <ScoreBadge score={result.totalScore} size="lg" showLabel />
          <div className="result-total__meta">
            <div className="result-total__title">総合スコア</div>
            {result.capped && (
              <div className="cap-badge">
                重要項目の指摘によりスコア上限が適用されています
                {result.rawScore != null && `（素点 ${result.rawScore}）`}
              </div>
            )}
            <div className="cwv-source">
              Core Web Vitals: {CWV_SOURCE_LABEL[result.cwvSource]}
            </div>
          </div>
        </div>

        <div className="result-cats">
          {result.categoryScores.map((c) => (
            <div
              key={c.category}
              className="cat-chip"
              title={c.available === false ? c.unavailableReason : undefined}
            >
              <span className="cat-chip__label">{c.label}</span>
              {c.available === false ? (
                <span className="cat-chip__score cat-chip__score--na">測定不可</span>
              ) : (
                <span className="cat-chip__score" style={{ color: scoreColor(c.score) }}>
                  {c.score}
                </span>
              )}
              <span className="cat-chip__pts muted small">
                {c.available === false ? '対象外' : `${c.weightedPoints}/${c.maxPoints}`}
              </span>
            </div>
          ))}
        </div>
      </div>

      {top3.length > 0 && (
        <div className="card public-top-issues">
          <h2>優先して改善すべき主要課題</h2>
          <ol className="public-top-issues__list">
            {top3.map((item) => (
              <li key={item.key} className="public-top-issue">
                <div className="public-top-issue__head">
                  <span
                    className="issue-item__status"
                    style={{
                      backgroundColor: item.status === 'fail' ? '#d7301f' : '#e0a800',
                    }}
                  >
                    {item.status === 'fail' ? 'NG' : '注意'}
                  </span>
                  <span className="public-top-issue__label">{item.label}</span>
                  {item.critical && <span className="issue-item__critical">重要</span>}
                  <span className="issue-item__cat">
                    {CATEGORY_LABELS[item.category]}
                  </span>
                </div>
                {item.advice && (
                  <p className="public-top-issue__advice">{item.advice}</p>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="public-cta card">
        <div>
          <h2 className="public-cta__title">詳しい改善提案は無料相談で</h2>
          <p className="muted small">
            スコアの根拠や優先度、具体的な改善手順について、担当者が無料でご相談に乗ります。
          </p>
        </div>
        <a className="btn btn--primary btn--lg" href={CONSULT_MAILTO}>
          無料で相談する
        </a>
      </div>

      <div className="result-grid">
        <div className="card">
          <h2>カテゴリ別評価</h2>
          <ScoreRadar categoryScores={result.categoryScores} />
        </div>
        <div className="card">
          <h2>課題リスト</h2>
          <IssueList items={result.items} />
        </div>
      </div>

      <div className="rubric-note">
        ※ コンテンツ品質などの評価は、Google公式ガイド（社内ナレッジ版）を基にした
        ルーブリック採点です。Googleの実際の評価・検索順位そのものではありません。
      </div>

      <div className="public-result__retry">
        <Link className="btn" to="/">
          別のURLを診断する
        </Link>
      </div>
    </div>
  );
}
